import QRCodeStyling from "qr-code-styling";
import { toStylingOptions } from "@/lib/qr";
import jsQR from "jsqr";

export const WATERMARK_TEXT = "QR Code Creator Studio";
const WM_LINES = ["QR Code", "Creator Studio"];

const STRATEGIES = [
  { pos: "br", scale: 0.85, square: false },
  { pos: "br", scale: 0.7, square: false },
  { pos: "br", scale: 0.55, square: false },
  { pos: "br", scale: 0.7, square: true },
  { pos: "br", scale: 0.55, square: true },
  { pos: "center", scale: 0.72, square: false },
  { pos: "center", scale: 0.6, square: true },
];

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

function saveBlob(blob, filename, ext) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `${filename}.${ext}`; a.click();
  URL.revokeObjectURL(url);
}
function blobToImage(blob) {
  return new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = URL.createObjectURL(blob); });
}
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath(); ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}
function canvasToBlob(canvas, mime) { return new Promise((res) => canvas.toBlob((b) => res(b), mime)); }
function decodeMatches(ctx, w, h, expected) {
  try {
    for (const size of [Math.min(w, h, 1024), 640, 320]) {
      if (size > w || size > h) continue;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const testCtx = canvas.getContext("2d", { willReadFrequently: true });
      testCtx.drawImage(ctx.canvas, 0, 0, size, size);
      const pixels = testCtx.getImageData(0, 0, size, size);
      const result = jsQR(pixels.data, size, size, {
        inversionAttempts: "attemptBoth",
      });
      if (result?.data === expected) return true;
    }
  } catch (error) {
    console.error("QR scan validation failed:", error);
  }
  return false;
}

// ---------- watermark chip ----------
function drawWatermark(ctx, size, margin, scale, pos) {
  const fs = Math.max(9, Math.round(size * 0.03 * scale));
  const lineH = Math.round(fs * 1.14), padX = Math.round(fs * 0.55), padY = Math.round(fs * 0.4);
  ctx.font = `600 ${fs}px Inter, Arial, sans-serif`;
  const textW = Math.max(...WM_LINES.map((l) => ctx.measureText(l).width));
  const w = Math.round(textW + padX * 2), h = Math.round(lineH * WM_LINES.length + padY * 2);
  const gap = Math.round(size * 0.012);
  let x1, y1;
  if (pos === "center") { x1 = Math.round((size - w) / 2); y1 = Math.round((size - h) / 2); }
  else { x1 = size - margin - gap - w; y1 = size - margin - gap - h; }
  const r = Math.round(fs * 0.4);
  roundRect(ctx, x1, y1, w, h, r); ctx.fillStyle = "rgba(255,255,255,0.97)"; ctx.fill();
  ctx.lineWidth = Math.max(1, size * 0.0022); ctx.strokeStyle = "rgba(0,0,0,0.16)"; ctx.stroke();
  ctx.fillStyle = "#0B0F17"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  WM_LINES.forEach((l, i) => ctx.fillText(l, x1 + w / 2, y1 + padY + lineH * i + lineH / 2));
  return { x1, y1, w, h, fs, padY, lineH, r };
}
function resolveRender(design) {
  const orig = design?.size || 320;
  const renderSize = Math.max(orig, 1024);
  const margin = Math.round((design?.margin ?? 12) * (renderSize / orig));
  return { renderSize, margin };
}
async function makeBase(design, dataString, square) {
  const { renderSize, margin } = resolveRender(design);
  const bd = { ...design, ecc: "H", size: renderSize, margin };
  if (square) { bd.dotType = "square"; bd.cornerSquareType = "square"; bd.cornerDotType = "square"; bd.useCustomCorners = false; }
  const qr = new QRCodeStyling({ ...toStylingOptions(bd, dataString), type: "canvas" });
  await wait(150);
  const img = await blobToImage(await qr.getRawData("png"));
  const canvas = document.createElement("canvas");
  canvas.width = img.width; canvas.height = img.height;
  const ctx = canvas.getContext("2d");
  if (design.transparent) { ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, canvas.width, canvas.height); }
  ctx.drawImage(img, 0, 0);
  return { canvas, ctx, size: img.width, margin, imgData: ctx.getImageData(0, 0, img.width, img.height) };
}
async function buildWatermarkedCanvas(design, dataString) {
  const styled = await makeBase(design, dataString, false);
  let squareBase = null;
  for (const s of STRATEGIES) {
    const b = s.square ? (squareBase || (squareBase = await makeBase(design, dataString, true))) : styled;
    b.ctx.putImageData(b.imgData, 0, 0);
    const geom = drawWatermark(b.ctx, b.size, b.margin, s.scale, s.pos);
    if (decodeMatches(b.ctx, b.size, b.size, dataString)) return { canvas: b.canvas, geom, size: b.size, square: s.square, ok: true };
  }
  return { canvas: styled.canvas, ok: false };
}
function buildSvgWatermark(g) {
  const cx = g.x1 + g.w / 2;
  const texts = WM_LINES.map((l, i) => `<text x="${cx}" y="${g.y1 + g.padY + g.lineH * i + g.lineH / 2}" font-family="Inter, Arial, sans-serif" font-weight="600" font-size="${g.fs}" fill="#0B0F17" text-anchor="middle" dominant-baseline="central">${l}</text>`).join("");
  return `<g><rect x="${g.x1}" y="${g.y1}" width="${g.w}" height="${g.h}" rx="${g.r}" fill="#ffffff" fill-opacity="0.97" stroke="#000000" stroke-opacity="0.16" stroke-width="1"/>${texts}</g>`;
}

// ---------- base QR canvas (validated) ----------
async function buildQRCanvas(design, dataString, watermark) {
  if (watermark) {
    const { canvas, ok } = await buildWatermarkedCanvas(design, dataString);
    if (!ok) throw new Error("watermark-unscannable");
    return canvas;
  }
  const size = Math.max(design.size || 320, 1024);
  const qr = new QRCodeStyling({ ...toStylingOptions({ ...design, size }, dataString), type: "canvas" });
  await wait(150);
  const img = await blobToImage(await qr.getRawData("png"));
  const c = document.createElement("canvas");
  c.width = img.width; c.height = img.height;
  const ctx = c.getContext("2d");
  ctx.drawImage(img, 0, 0);
  // decode-validate on a white-backed copy (handles transparent bg)
  const t = document.createElement("canvas");
  t.width = c.width; t.height = c.height;
  const tc = t.getContext("2d");
  tc.fillStyle = "#FFFFFF"; tc.fillRect(0, 0, t.width, t.height); tc.drawImage(c, 0, 0);
  if (!decodeMatches(tc, t.width, t.height, dataString)) throw new Error("scan-failed");
  return c;
}

// ---------- frame geometry (shared by canvas + svg) ----------
function frameGeom(q, design) {
  const framed = design.frame && design.frame !== "none";
  if (!framed) return { framed: false, totalW: q, totalH: q, qrX: 0, qrY: 0 };
  const labelTop = design.frame === "scan-top" || design.frame === "badge";
  const pad = Math.round(q * 0.05);
  const border = Math.max(3, Math.round(q * 0.012));
  const radius = Math.round(q * 0.06);
  const labelH = Math.round(q * 0.13);
  const gap = Math.round(q * 0.035);
  const totalW = q + pad * 2 + border * 2;
  const totalH = border * 2 + pad * 2 + q + labelH + gap;
  const qrX = Math.round((totalW - q) / 2);
  const qrY = labelTop ? border + pad + labelH + gap : border + pad;
  const labelY = labelTop ? border + pad : qrY + q + gap;
  const lx = border + pad, lw = totalW - 2 * border - 2 * pad;
  return { framed: true, labelTop, pad, border, radius, labelH, gap, totalW, totalH, qrX, qrY, labelY, lx, lw };
}
function drawFrameCanvas(qrCanvas, design) {
  const q = qrCanvas.width;
  const g = frameGeom(q, design);
  if (!g.framed) return qrCanvas;
  const canvas = document.createElement("canvas");
  canvas.width = g.totalW; canvas.height = g.totalH;
  const ctx = canvas.getContext("2d");
  roundRect(ctx, 0, 0, g.totalW, g.totalH, g.radius + g.border); ctx.fillStyle = design.frameColor || "#6366F1"; ctx.fill();
  roundRect(ctx, g.border, g.border, g.totalW - 2 * g.border, g.totalH - 2 * g.border, g.radius); ctx.fillStyle = design.frameBg || "#FFFFFF"; ctx.fill();
  ctx.drawImage(qrCanvas, g.qrX, g.qrY, q, q);
  roundRect(ctx, g.lx, g.labelY, g.lw, g.labelH, Math.round(g.labelH * 0.26)); ctx.fillStyle = design.frameColor || "#6366F1"; ctx.fill();
  ctx.fillStyle = design.frameTextColor || "#FFFFFF"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.font = `700 ${Math.round(g.labelH * 0.42)}px Inter, Arial, sans-serif`;
  try { ctx.letterSpacing = `${Math.round(g.labelH * 0.06)}px`; } catch {}
  ctx.fillText(design.frameText || "SCAN ME", g.totalW / 2, g.labelY + g.labelH / 2, g.lw - 12);
  return canvas;
}
function frameSvgMarkup(q, design) {
  const g = frameGeom(q, design);
  if (!g.framed) return null;
  const fx = design.frameColor || "#6366F1";
  const label = `<rect x="${g.lx}" y="${g.labelY}" width="${g.lw}" height="${g.labelH}" rx="${Math.round(g.labelH * 0.26)}" fill="${fx}"/>` +
    `<text x="${g.totalW / 2}" y="${g.labelY + g.labelH / 2}" font-family="Inter, Arial, sans-serif" font-weight="700" font-size="${Math.round(g.labelH * 0.42)}" letter-spacing="${Math.round(g.labelH * 0.06)}" fill="${design.frameTextColor || "#FFFFFF"}" text-anchor="middle" dominant-baseline="central">${(design.frameText || "SCAN ME").replace(/[<>&]/g, "")}</text>`;
  const outer = `<rect x="0" y="0" width="${g.totalW}" height="${g.totalH}" rx="${g.radius + g.border}" fill="${fx}"/>` +
    `<rect x="${g.border}" y="${g.border}" width="${g.totalW - 2 * g.border}" height="${g.totalH - 2 * g.border}" rx="${g.radius}" fill="${design.frameBg || "#FFFFFF"}"/>`;
  return { g, outer, label };
}

// ---------- SVG export (frame as real SVG) ----------
async function buildQRSvg(design, dataString, watermark) {
  if (watermark) {
    const { geom, size, square, ok } = await buildWatermarkedCanvas(design, dataString);
    if (!ok) throw new Error("watermark-unscannable");
    const { renderSize, margin } = resolveRender(design);
    const bd = { ...design, ecc: "H", size: renderSize, margin };
    if (square) { bd.dotType = "square"; bd.cornerSquareType = "square"; bd.cornerDotType = "square"; bd.useCustomCorners = false; }
    const qr = new QRCodeStyling({ ...toStylingOptions(bd, dataString), type: "svg" });
    await wait(150);
    let svg = await (await qr.getRawData("svg")).text();
    svg = svg.replace("</svg>", buildSvgWatermark(geom) + "</svg>");
    return { svg, q: size };
  }
  await buildQRCanvas(design, dataString, false); // validate scannability first
  const size = Math.max(design.size || 320, 1024);
  const qr = new QRCodeStyling({ ...toStylingOptions({ ...design, size }, dataString), type: "svg" });
  await wait(150);
  return { svg: await (await qr.getRawData("svg")).text(), q: size };
}
async function buildFramedSvgBlob(design, dataString, watermark) {
  const { svg, q } = await buildQRSvg(design, dataString, watermark);
  if (!design.frame || design.frame === "none") return new Blob([svg], { type: "image/svg+xml" });
  const fm = frameSvgMarkup(q, design);
  const inner = svg.replace(/^[\s\S]*?<svg[^>]*>/, "").replace(/<\/svg>\s*$/, "");
  const nested = `<svg x="${fm.g.qrX}" y="${fm.g.qrY}" width="${q}" height="${q}" viewBox="0 0 ${q} ${q}">${inner}</svg>`;
  const out = `<svg xmlns="http://www.w3.org/2000/svg" width="${fm.g.totalW}" height="${fm.g.totalH}" viewBox="0 0 ${fm.g.totalW} ${fm.g.totalH}">${fm.outer}${nested}${fm.label}</svg>`;
  return new Blob([out], { type: "image/svg+xml" });
}

// ---------- public API ----------
export async function downloadQR(design, dataString, filename, ext = "png", watermark = false) {
  if (ext === "svg") { saveBlob(await buildFramedSvgBlob(design, dataString, watermark), filename, "svg"); return; }
  const framed = drawFrameCanvas(await buildQRCanvas(design, dataString, watermark), design);
  const jpg = ext === "jpg" || ext === "jpeg";
  if (jpg) {
    const w = document.createElement("canvas"); w.width = framed.width; w.height = framed.height;
    const wc = w.getContext("2d"); wc.fillStyle = "#FFFFFF"; wc.fillRect(0, 0, w.width, w.height); wc.drawImage(framed, 0, 0);
    saveBlob(await canvasToBlob(w, "image/jpeg"), filename, "jpg"); return;
  }
  saveBlob(await canvasToBlob(framed, "image/png"), filename, ext);
}

export async function getRawBlob(design, dataString, ext = "png", watermark = false) {
  if (ext === "svg") return await buildFramedSvgBlob(design, dataString, watermark);
  const framed = drawFrameCanvas(await buildQRCanvas(design, dataString, watermark), design);
  const jpg = ext === "jpg" || ext === "jpeg";
  if (jpg) {
    const w = document.createElement("canvas"); w.width = framed.width; w.height = framed.height;
    const wc = w.getContext("2d"); wc.fillStyle = "#FFFFFF"; wc.fillRect(0, 0, w.width, w.height); wc.drawImage(framed, 0, 0);
    return await canvasToBlob(w, "image/jpeg");
  }
  return await canvasToBlob(framed, "image/png");
}

export async function printQR(design, dataString, watermark = false) {
  const framed = drawFrameCanvas(await buildQRCanvas(design, dataString, watermark), design);
  const url = framed.toDataURL("image/png");
  const logo = `${window.location.origin}/brand/qr-code-creator-studio-light.png`;
  const w = window.open("", "_blank");
  if (!w) throw new Error("popup-blocked");
  w.document.write(`<html><head><title>QR Code Creator Studio</title></head><body style="margin:0;display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:100vh;background:#fff;font-family:Inter,Arial,sans-serif"><img src="${logo}" alt="QR Code Creator Studio" style="height:44px;margin-bottom:26px" onerror="this.style.display='none'"/><img src="${url}" style="max-width:80vw;max-height:72vh"/><script>window.onload=function(){setTimeout(function(){window.print();},350);}<\/script></body></html>`);
  w.document.close();
}
